/**
 * TrackSelector
 *
 * Horizontal tab bar for selecting a discipline service track. Each tab shows the discipline code,
 * a phase badge (Onboarding / In care / Discharged), a red or amber PA chip when the prior
 * authorization has ended or ends soon, and the next step in a few words.
 * Tabs scroll horizontally on mobile (overflow-x-auto).
 *
 * Default discipline dot: shown on the tab matching the user's providerType.
 * Overview v3: the four-segment mini bar is gone (the phase badge says the same thing, D8/F8).
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import type { ServiceTrack, Discipline } from './types';
import { DISCIPLINE_COLORS, DISCIPLINE_DISPLAY, DISCIPLINE_ORDER, PHASE_BADGE, getSubstateLabel, trackDisplayPhase, trackPaFlag } from './types';
import { optionalTrackDisciplines } from '../../../constants/disciplines';

interface TrackSelectorProps {
  tracks:             ServiceTrack[];
  selectedDiscipline: Discipline | null;
  defaultDiscipline?: Discipline | null;
  onSelect:           (discipline: Discipline) => void;
  /** Optional disciplines (MSW, HHA, …) not ordered yet: listed under "Add" (D-10) */
  addableDisciplines?: Discipline[];
  /** Order an optional discipline (opens a new referral for it) */
  onAddDiscipline?:   (discipline: Discipline) => void;
}

/** Short tab label: the next onboarding step, or the onboarding count for older responses. */
export function trackStepLabel(track: ServiceTrack): string {
  const phase = trackDisplayPhase(track);
  if (phase !== 'ONBOARDING') return '';
  if (track.nextStep) return getSubstateLabel(track.nextStep);
  const flags = Object.values(track.socChecklist || {}).filter((v): v is boolean => typeof v === 'boolean');
  const done = track.completedCount ?? flags.filter(Boolean).length;
  const total = track.totalCount ?? flags.length;
  if (total > 0 && done >= total) return 'Ready to start';
  return `${done}/${total} done`;
}

/** Tracks shown as tabs, in registry order. Optional disciplines only show once ordered. */
export function visibleTracks(tracks: ServiceTrack[]): ServiceTrack[] {
  const optional = optionalTrackDisciplines() as Discipline[];
  return DISCIPLINE_ORDER
    .map(d => tracks.find(t => t.discipline === d))
    .filter((t): t is ServiceTrack => Boolean(t) && !(optional.includes(t!.discipline) && t!.phase === 'NOT_ORDERED'));
}

/**
 * Default tab (D7): the user's own discipline, else the first track that needs action, else the
 * first track in care, else the first track.
 */
export function defaultTrackDiscipline(tracks: ServiceTrack[], providerDiscipline?: Discipline | null): Discipline | null {
  const shown = visibleTracks(tracks);
  if (shown.length === 0) return null;
  const own = providerDiscipline ? shown.find(t => t.discipline === providerDiscipline) : undefined;
  if (own) return own.discipline;
  const needsAction = shown.find(t => t.needsAction ?? (trackDisplayPhase(t) === 'ONBOARDING' || trackPaFlag(t) !== null));
  if (needsAction) return needsAction.discipline;
  const inCare = shown.find(t => trackDisplayPhase(t) === 'IN_CARE');
  return (inCare ?? shown[0]).discipline;
}

const TrackSelector: React.FC<TrackSelectorProps> = ({
  tracks,
  selectedDiscipline,
  defaultDiscipline,
  onSelect,
  addableDisciplines = [],
  onAddDiscipline,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const shown = visibleTracks(tracks);

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-2 px-4 py-2 min-w-max" role="tablist" aria-label="Discipline services">
        {shown.map(track => {
          const disc      = track.discipline;
          const colors    = DISCIPLINE_COLORS[disc];
          const badge     = PHASE_BADGE[trackDisplayPhase(track)];
          const paFlag    = trackPaFlag(track);
          const step      = trackStepLabel(track);
          const selected  = selectedDiscipline === disc;
          const isDefault = defaultDiscipline === disc;

          return (
            <button
              key={disc}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect(disc)}
              className={[
                'relative flex flex-col items-start px-3 py-2 rounded-lg border-2 min-w-[96px] min-h-[44px] transition-all',
                'focus:outline-none focus:ring-2 focus:ring-offset-1',
                selected
                  ? `${colors.bg} ${colors.strongBorder}`
                  : `bg-white ${colors.midBorder} hover:bg-slate-50`,
              ].join(' ')}
            >
              {isDefault && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-slate-700" aria-label="Your discipline" />
              )}
              <span className={['text-sm font-bold', selected ? colors.text : 'text-slate-600'].join(' ')}>{disc}</span>
              <span className="flex flex-wrap gap-1 mt-0.5">
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${badge.className}`}>{badge.label}</span>
                {paFlag && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${paFlag === 'EXPIRED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                    {paFlag === 'EXPIRED' ? 'PA ended' : 'PA ends soon'}
                  </span>
                )}
              </span>
              {step && (
                <span className={['text-xs mt-0.5 truncate max-w-[120px]', selected ? colors.text : 'text-slate-500'].join(' ')}>{step}</span>
              )}
            </button>
          );
        })}

        {/* Add — order an optional discipline that has no track yet (D-10: opens a new referral) */}
        {onAddDiscipline && addableDisciplines.length > 0 && (
          <div className="relative self-center shrink-0">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={addOpen}
              onClick={() => setAddOpen(open => !open)}
              className="flex items-center gap-1 px-2 py-2 rounded-lg text-xs text-slate-500 border border-dashed border-slate-300 hover:bg-slate-50 transition-colors"
            >
              <Plus className="w-3 h-3" />Add
            </button>
            {addOpen && (
              <div role="menu" className="absolute left-0 top-full mt-1 z-20 w-56 rounded-md border border-slate-200 bg-white shadow-lg py-1">
                <p className="px-3 py-1 text-xs text-slate-500">Order a service (opens a new referral)</p>
                {addableDisciplines.map(d => (
                  <button
                    key={d}
                    type="button"
                    role="menuitem"
                    onClick={() => { setAddOpen(false); onAddDiscipline(d); }}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {DISCIPLINE_DISPLAY[d] || d}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackSelector;
